import {systemDateFormat} from '@lib/utils';
import {createSelector} from '@reduxjs/toolkit';
import format from 'date-fns/format';
import getISOWeek from 'date-fns/getISOWeek';
import parseISO from 'date-fns/parseISO';
import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {groupWorkspaceReservationAdapter, workspaceReservationsAdapter} from './adapters';
import {WorkdayWithExtraInfo, WorkspaceReservationLocation} from './types';
import {workspaceReservationOnDay} from './utils';
import isNumber from 'lodash/isNumber';
import {getAllWorkdays} from '../workday';
import {getConnectionGroupedByDate} from '../connections';
import {getParkingReservationsGroupedByDate} from '../parkingReservation';
import {isAfter} from 'date-fns';
import groupBy from 'lodash/groupBy';
import {cachedDate, isDefinedArray} from '../../util';
import {getBuildingById} from '../buildings';
import {getFloorById} from '../floors';
import {getAreaById} from '../areas';
import {getDeskById} from '../desks';

export const getIsLoadingWorkspaceReservations = (state: RootState) => state.data.workspaceReservations.isLoading;

export const {
  selectById: getWorkspaceReservationById,
  selectIds: getWorkspaceReservationsIds,
  selectEntities: getWorkspaceReservationEntities,
  selectAll: getAllWorkspaceReservations,
  selectTotal: getTotalWorkspaceReservations,
} = workspaceReservationsAdapter.getSelectors(
  (state: RootState) => state.data.workspaceReservations.workspaceReservations,
);
export const {selectAll: getAllGroupWorkspaceReservations, selectById: getGroupWorkspaceReservationById} =
  groupWorkspaceReservationAdapter.getSelectors(
    (state: RootState) => state.data.workspaceReservations.groupWorkspaceReservations,
  );

export const getWorkspaceReservationByDate = createCachedSelector(
  getAllWorkspaceReservations,
  (_: RootState, date: Date | undefined) => cachedDate(date),
  (allWorkspaceReservations, date) => {
    if (!date) {
      return;
    }
    return allWorkspaceReservations.find(
      (workspaceReservation) =>
        workspaceReservationOnDay(date)(workspaceReservation) &&
        workspaceReservation.currentMember?.status === 'Confirmed',
    );
  },
)((_, date) => `${date?.toISOString()}` || '');

export const getPendingWorkspaceReservationsByDate = createCachedSelector(
  getAllWorkspaceReservations,
  (_: RootState, date: Date | undefined) => date,
  (allWorkspaceReservations, date) => {
    if (!date) {
      return;
    }
    return allWorkspaceReservations.filter((workspaceReservation) => {
      const reservationForDateIsPending =
        workspaceReservationOnDay(date)(workspaceReservation) &&
        workspaceReservation.currentMember?.status === 'InvitationPending';

      return (
        reservationForDateIsPending &&
        workspaceReservation.currentMember &&
        isNumber(workspaceReservation.currentMember.remainingDaysToReply) &&
        workspaceReservation.currentMember.remainingDaysToReply >= 0
      );
    });
  },
)((_, date) => date?.toISOString() || '');

export const getIsUserOwnerOfGroupWorkspaceReservationByDate = createCachedSelector(
  getWorkspaceReservationByDate,
  (_: RootState, date: Date) => date,
  (workspaceReservation) => {
    return !!workspaceReservation?.groupReservationId && !!workspaceReservation?.currentMember?.isOwner;
  },
)((_, date) => date?.toISOString() || '');

export const getIsUserOwnerOfWorkspaceReservationByDate = createCachedSelector(
  getWorkspaceReservationByDate,
  (_: RootState, date: Date) => cachedDate(date),
  (workspaceReservation) => {
    return !!workspaceReservation?.currentMember?.isOwner;
  },
)((_, date) => date?.toISOString() || '');

export const getAllWorkspaceReservationCountPerNodeOnDate = (state: RootState) =>
  state.data.workspaceReservations.workspaceReservationCountPerNodeOnDate;

export const getWorkspaceReservationCountPerNodeOnDate = createCachedSelector(
  getAllWorkspaceReservationCountPerNodeOnDate,
  (_: RootState, date: Date) => date,
  (workspaceReservationCountPerNodeOnDate, date) => {
    const formattedDate = format(date, systemDateFormat);
    return workspaceReservationCountPerNodeOnDate[formattedDate];
  },
)((_: RootState, date: Date) => date.toISOString());

export const getLastSelectedWorkspaceReservationLocation = createSelector(
  (state: RootState) => getBuildingById(state, state.data.workspaceReservations.lastSelectedLocation.buildingId),
  (state: RootState) => getFloorById(state, state.data.workspaceReservations.lastSelectedLocation.floorId),
  (state: RootState) => getAreaById(state, state.data.workspaceReservations.lastSelectedLocation.areaId),
  (state: RootState) => getDeskById(state, state.data.workspaceReservations.lastSelectedLocation.deskId),
  (building, floor, area, desk) => {
    return {buildingId: building?.id || '', floorId: floor?.id || '', areaId: area?.id || '', deskId: desk?.id || ''};
  },
);

export const getIsLastSelectedWorkspaceReservationLocationLoaded = (state: RootState): boolean =>
  state.data.workspaceReservations.isLastSelectedLocationLoaded;

export const getWorkspaceReservationsGroupedByWeek = createSelector(
  getAllWorkspaceReservations,
  (workspaceReservations) => {
    const confirmedWorkspaceReservations = workspaceReservations.filter(
      (workspaceReservation) => workspaceReservation.status === 'Confirmed',
    );
    return groupBy(confirmedWorkspaceReservations, (workspaceReservation) =>
      getISOWeek(parseISO(workspaceReservation.startDate)),
    );
  },
);

export const getWorkspaceReservationCountByWeekNumber = createSelector(
  getWorkspaceReservationsGroupedByWeek,
  (_state: RootState, weekNumber: number) => weekNumber,
  (workspaceReservationsByWeek, weekNumber) => {
    const workspaceReservationsOnWeek = workspaceReservationsByWeek[weekNumber];
    return workspaceReservationsOnWeek ? workspaceReservationsOnWeek.length : 0;
  },
);

export const getIsCreatingWorkspaceReservation = (state: RootState) => state.data.workspaceReservations.isCreating;

export const getWorkspaceReservationUpdates = (state: RootState) => state.data.workspaceReservations.updates;

export const getWorkspaceReservationDeletions = (state: RootState) => state.data.workspaceReservations.deletions;

export const getIsUpdatingWorkspaceReservationById = createCachedSelector(
  getWorkspaceReservationUpdates,
  (_state: RootState, workspaceReservationId: string) => workspaceReservationId,
  (updating, workspaceReservationId) => (updating && updating[workspaceReservationId]) || false,
)((_state, workspaceReservationId) => workspaceReservationId);

export const getIsDeletingWorkspaceReservationById = createCachedSelector(
  getWorkspaceReservationDeletions,
  (_state: RootState, workspaceReservationId: string) => workspaceReservationId,
  (deleting, workspaceReservationId) => (deleting && deleting[workspaceReservationId]) || false,
)((_state, workspaceReservationId) => workspaceReservationId);

export const getGroupWorkspaceSearchedInvitees = (state: RootState) =>
  state.data.workspaceReservations.searchedInvitees;

export const getGroupWorkspaceInviteesSearchStatus = (state: RootState) =>
  state.data.workspaceReservations.inviteesSearchStatus;

export const getIsDeletingRegistration = (state: RootState) => state.data.workspaceReservations.isDeletingRegistration;

export const getIsDeletingTodayRegistration = (state: RootState) =>
  state.data.workspaceReservations.isDeletingTodayRegistration;

export const getWorkspaceReservationsGroupedByDate = createSelector(
  getAllWorkspaceReservations,
  (allWorkspaceReservations) => {
    return groupBy(allWorkspaceReservations, 'startDate');
  },
);

// Because of the weird issues with imports this selector needs to live in this file for now.
export const getFutureWorkdaysWithExtraInformation = createCachedSelector(
  [
    getAllWorkdays,
    getConnectionGroupedByDate,
    getParkingReservationsGroupedByDate,
    getWorkspaceReservationsGroupedByDate,
    (_state: RootState, date: string) => date,
  ],
  (
    workdays,
    connectionWorkdaysGroupedByDate,
    parkingReservationsGroupedByDate,
    workspaceReservationsGroupedByDate,
    date,
  ) => {
    const futureWorkdays = workdays.filter((workDay) => isAfter(new Date(workDay.startDate), new Date(date)));

    const futureWorkdaysWithConnections: Array<WorkdayWithExtraInfo> = [];

    for (let futureWorkday of futureWorkdays) {
      let futureWokrkdayWithExtras: WorkdayWithExtraInfo = {
        ...futureWorkday,
        connections: [],
        parking: null,
        workspaceReservation: null,
      };

      const legalConnections = connectionWorkdaysGroupedByDate[futureWorkday.startDate]
        ?.filter((connectionWk) => connectionWk.nodeId === futureWorkday.nodeId)
        .map((lgConnectionWk) => lgConnectionWk.user);
      const parking = parkingReservationsGroupedByDate[futureWorkday.startDate];
      const workspaceReservation = workspaceReservationsGroupedByDate[futureWorkday.startDate];

      if (legalConnections?.length && isDefinedArray(legalConnections)) {
        futureWokrkdayWithExtras.connections = legalConnections;
      }

      if (parking?.length) {
        futureWokrkdayWithExtras.parking = parking[0];
      }

      if (workspaceReservation?.length) {
        futureWokrkdayWithExtras.workspaceReservation = workspaceReservation[0];
      }

      futureWorkdaysWithConnections.push(futureWokrkdayWithExtras);
    }
    return futureWorkdaysWithConnections;
  },
)((_state, date) => date);
